<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card class="px-3 mt-4 pt-7">
          <SimplePageHeader :title="pageTitle" class="px-4 pr-5 mb-5">
            <template v-slot:right-buttons>
              <v-btn
                v-if="isEditing"
                color="primary"
                @click="handlePreview"
              >
                <v-icon left>visibility</v-icon>Preview
              </v-btn>
            </template>
          </SimplePageHeader>
          <v-card-text>
            <v-form ref="detailsForm">
              <h2 class="mb-4">Details</h2>
              <v-row>
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="name"
                    label="Template Name *"
                    outlined
                    :rules="[Required('Template Name')]"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-switch v-model="isActive" label="Active" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="documentTypeId"
                    :items="documentTypeList"
                    item-text="name"
                    item-value="id"
                    label="Document Type *"
                    outlined
                    :rules="[Required('Document Type')]"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <ClientsDropDownList
                    :id="clientId"
                    :clearable="true"
                    @clientIdChange="clientId = $event"
                  />
                </v-col>
              </v-row>
            </v-form>
            <h2 class="mb-4">Document Content</h2>
            <v-row>
              <v-col cols="12" md="9">
                <quill-editor
                  ref="quillEditor"
                  v-model="htmlBody"
                  :options="quillOptions"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined>
                  <v-card-title class="subtitle-1">Insert Token</v-card-title>
                  <v-card-subtitle>Click a token to insert it at the cursor position</v-card-subtitle>
                  <v-list dense class="token-list">
                    <v-list-item
                      v-for="token in tokens"
                      :key="token.name"
                      @click="insertToken(token.name)"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ '{' + token.name + '}' }}</v-list-item-title>
                        <v-list-item-subtitle>{{ token.description }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <ButtonsSubmitForm
          v-if="!isReadOnlyAccess"
          :isFormValid="true"
          @save="handleSave"
          @cancel="cancel"
          />
      </v-col>
    </v-row>
    <BottomSpacer />
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { documentTemplatesRoute } from '@/lib/constants/routes'
import { PATH_DOCUMENT_TEMPLATES } from '@/lib/constants/api'
import { SimplePageHeader } from '@/components/pageComponents'
import { ButtonsSubmitForm, BottomSpacer } from '@/components/buttons'
import { ClientsDropDownList } from '@/components/clients'
import getApiLocation from '@/lib/api/getApiLocation'
import { getAuthToken } from '@/lib/authentication'
import Required from '@/lib/formValidations/required'

const DOCUMENT_TOKENS = [
  { name: 'CandidateFirstName', description: 'Candidate\'s first name' },
  { name: 'CandidateLastName', description: 'Candidate\'s last name' },
  { name: 'CandidateFullName', description: 'Candidate\'s full name' },
  { name: 'CandidateEmail', description: 'Candidate\'s email address' },
  { name: 'ClientName', description: 'Client company name' },
  { name: 'JobTitle', description: 'Job title for the assignment/placement' },
  { name: 'StartDate', description: 'Assignment or placement start date' },
  { name: 'EndDate', description: 'Assignment end date' },
  { name: 'ContractType', description: 'Contract type' },
  { name: 'PayRate', description: 'Pay rate' },
  { name: 'BillRate', description: 'Bill rate' },
  { name: 'PlacementDate', description: 'Direct placement date' },
  { name: 'PlacementSalary', description: 'Direct placement salary' },
  { name: 'RecruiterName', description: 'Recruiter\'s name' },
  { name: 'CompanyName', description: 'CTI company name' },
  { name: 'CompanyAddress', description: 'CTI company address' },
  { name: 'TodayDate', description: 'Today\'s date' },
  { name: 'DocumentTypeName', description: 'Name of the document type' }
]

export default {
  components: {
    SimplePageHeader,
    ButtonsSubmitForm,
    BottomSpacer,
    ClientsDropDownList
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.previousRoute = from
    })
  },
  data () {
    return {
      documentTemplatesRoute,
      tokens: DOCUMENT_TOKENS,
      quillOptions: {
        theme: 'snow'
      },
      previousRoute: null
    }
  },
  computed: {
    ...mapFields('documentTemplate', [
      'id',
      'name',
      'documentTypeId',
      'clientId',
      'htmlBody',
      'isActive'
    ]),
    ...mapFields('documentTypes', ['documentTypeList']),
    isEditing () {
      return !!this.$route.params.id
    },
    templateIdAsInt () {
      return parseInt(this.$route.params.id) || this.id
    },
    pageTitle () {
      return this.isEditing ? 'Edit Document Template' : 'New Document Template'
    },
    saveButtonLabel () {
      return this.isEditing ? 'Update' : 'Save'
    }
  },
  methods: {
    ...mapActions('documentTemplate', ['getDocumentTemplate', 'saveDocumentTemplate', 'clearDocumentTemplate']),
    ...mapActions('documentTypes', ['getDocumentTypes']),
    Required,
    handleSave () {
      if (!this.$refs.detailsForm.validate()) return
      this.saveDocumentTemplate()
        .then(response => {
          if (response.data) {
            this.$router.push(documentTemplatesRoute.url)
          }
        })
        .catch(() => {})
    },
    cancel () {
      if (this.previousRoute && this.previousRoute.name === documentTemplatesRoute.name) {
        this.$router.back()
        return
      }
      this.$router.push({ name: documentTemplatesRoute.name })
    },
    handlePreview () {
      // window.open() can't attach a custom Authorization header, so the token travels via
      // query string — the API's ApiAuthentication filter accepts either for this reason.
      const url = `${getApiLocation()}/${PATH_DOCUMENT_TEMPLATES}/${this.templateIdAsInt}/preview?access_token=${encodeURIComponent(getAuthToken())}`
      window.open(url, '_blank')
    },
    insertToken (tokenName) {
      const editor = this.$refs.quillEditor && this.$refs.quillEditor.quill
      if (!editor) return
      const range = editor.getSelection()
      const index = range ? range.index : editor.getLength()
      editor.insertText(index, `{${tokenName}}`)
      editor.setSelection(index + tokenName.length + 2)
    }
  },
  created () {
    this.getDocumentTypes()
    if (this.isEditing) {
      this.getDocumentTemplate({ id: this.$route.params.id })
    } else {
      this.clearDocumentTemplate()
    }
  }
}
</script>

<style scoped>
.token-list {
  max-height: 480px;
  overflow-y: auto;
}
</style>
